import React, { useState } from 'react';
import { Box, Button, Typography, TextField, MenuItem, Select, Drawer, IconButton, styled, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomModal from '../../CustomModal';
import { closeModal } from '../../../redux/slices/profileSlice';
import palette from '../../../theme/palette';
import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../Iconify';
import CustomDrawer from '../../CustomDrawer';
import Image from '../../Image';
import Card from '../../../assets/profile/card.png';
import useLocales from '../../../hooks/useLocales';
import AffinBank from '../../../assets/banks/AffinBank.png';
import AllianceBank from '../../../assets/banks/AllianceBank.png';
import AMBank from '../../../assets/banks/AMBank.png';
import ArgoBank from '../../../assets/banks/ArgoBank.png';
import BankIslamMalaysia from '../../../assets/banks/BankIslamMalaysia.png';
import BankMuamalat from '../../../assets/banks/BankMuamalat.png';
import BankRakyat from '../../../assets/banks/BankRakyat.png';
import BSNBank from '../../../assets/banks/BSNBank.png';
import CIMBBank from '../../../assets/banks/CIMBBank.png';
import CitiBank from '../../../assets/banks/CitiBank.png';
import GXBank from '../../../assets/banks/GXBank.png';
import HongLeongBank from '../../../assets/banks/HongLeongBank.png';
import HSBCBank from '../../../assets/banks/HSBCBank.png';
import Maybank from '../../../assets/banks/Maybank.png';
import MerchanttradeBank from '../../../assets/banks/MerchantradeBank.png';
import OCBCBank from '../../../assets/banks/OCBCBank.png';
import PublicBank from '../../../assets/banks/PublicBank.png';
import RHBBank from '../../../assets/banks/RHBBank.png';
import SecurePay from '../../../assets/banks/SecurePay.png';
import StandardCharteredBank from '../../../assets/banks/StandardCharteredBank.png';
import TouchGo from '../../../assets/banks/TouchGo.png';
import UOBBank from '../../../assets/banks/UOBBank.png';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';
import { conObjForm } from '../../../utils/connection-assistant';

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: '#F3F4F6',
    height: 48,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #E5E7EB',
    },
  },
  '& input': {
    padding: '12px',
  },
  '& input::placeholder': {
    color: '#9CA3AF',
    opacity: 1,
  },
};

const banksIcons = {
  maybank: Maybank,
  cimbbank: CIMBBank,
  publicbank: PublicBank,
  hongleongbank: HongLeongBank,
  rhbbank: RHBBank,
  ambank: AMBank,
  affinbank: AffinBank,
  bsnbank: BSNBank,
  alliancebank: AllianceBank,
  bankislammalaysia: BankIslamMalaysia,
  hsbcbank: HSBCBank,
  uobbank: UOBBank,
  citibank: CitiBank,
  ocbcbank: OCBCBank,
  standardcharteredbank: StandardCharteredBank,
  argobank: ArgoBank,
  bankmuamalat: BankMuamalat,
  bankrakyat: BankRakyat,
  gxbank: GXBank,
  merchanttradebank: MerchanttradeBank,
  securepay: SecurePay,
  touchandgo: TouchGo,
};

const getBankKey = (name) => {
  return name
    ?.toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '')
    .replace(/[^a-z]/g, '');
};

const FormSection = styled(Box)({
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  backgroundColor: '#fff',
  borderRadius: 8,
});

const AddBankAccountDialog = ({ handleClose, handleOpen }) => {
  const { translate } = useLocales();
  const isDesktop = useResponsive('up', 'lg');
  const [prefferedOpen, setPrefferedOpen] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  const [holder, setHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const isValid = holder.trim() !== '' && accountNumber.trim() !== '';

  const { bank } = useSelector((state) => state.lookup);

  console.log(bank);

  const selectedBank = bank?.find((b) => b.bank_id === selectedBankId);
  const selectedIcon = banksIcons[getBankKey(selectedBank?.bank_name)] || Card;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const payload = {
        'add_user_withdraw_bank[bank_id]': selectedBankId,
        'add_user_withdraw_bank[account_holder_name]': holder,
        'add_user_withdraw_bank[account_number]': accountNumber,
        'add_user_withdraw_bank[default]': 2,
      };

      const { data: response } = await conObjForm.post('v1/user/add-withdraw-bank', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        enqueueSnackbar(response?.message || 'Bank added successfully', {
          variant: 'success',
        });

        clearFields();
        dispatch(closeModal());
      } else {
        enqueueSnackbar(response?.message || 'Add Bank failed', { variant: 'error' });
      }
    } catch (error) {
      const data = error.response?.data;

      if (data?.error && Array.isArray(data.error)) {
        data.error.forEach((err) => {
          enqueueSnackbar(err, { variant: 'error' });
        });
      } else if (data?.message) {
        enqueueSnackbar(data.message, { variant: 'error' });
      } else {
        enqueueSnackbar('An error occurred during submission', {
          variant: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFields = () => {
    setSelectedBankId(null);
    setHolder('');
    setAccountNumber('');
  };

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          open={handleOpen}
          onClose={() => {
            handleClose();
            clearFields();
          }}
          actions={
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isValid || loading}
              fullWidth
              sx={{
                background: theme.palette.gradients.buttonPrimary,
                textTransform: 'none',
                borderRadius: '30px',
                fontWeight: 600,
                fontSize: '16px',
                color: '#1a1a1a',
                boxShadow: 'none',

                '&:hover': {
                  background: theme.palette.gradients.buttonPrimary2,
                  boxShadow: 'none',
                },

                '&.Mui-disabled': {
                  background: '#F3F4F6',
                  color: '#9CA3AF',
                },
              }}
            >
              {translate('submit')}
            </Button>
          }
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Title */}
            <Typography variant="h6" fontWeight={600} fontSize={'24px'} textAlign="center">
              {translate('add_bank_account')}
            </Typography>

            {/* Preferred Bank */}
            <Box sx={{ display: 'flex', gap: '12px', flexDirection: 'column', marginTop: '32px' }}>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('preferred_bank')}
                </Typography>

                <Select
                  fullWidth
                  value={selectedBankId}
                  onChange={(e) => setSelectedBankId(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <img src={Card} alt="bank card" width={40} height={40} />
                          {translate('select_bank')}
                        </Box>
                      );
                    }

                    const selectedBank = bank.find((b) => b.bank_id === selected);
                    const icon = banksIcons[getBankKey(selectedBank?.bank_name)] || Card;

                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <img src={icon} width={40} alt="icon" height={40} />
                        {selectedBank?.bank_name}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 280,
                        overflowY: 'auto',
                        borderRadius: '10px',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#F3F4F6',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#A0A0A0',
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#F3F4F6',
                    ...(selectedBankId !== '' && {
                      // border: `1px solid ${theme.palette.primary.main}`,
                      background: `${theme.palette.gradients.buttonPrimary} !important`,
                    }),
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 600,
                    },
                    '& .MuiSelect-icon': { color: '#000' },
                  }}
                >
                  {/* Default */}
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <img src={Card} width={40} alt="card" height={40} />
                      {translate('select_bank')}
                    </Box>
                  </MenuItem>

                  {/* Banks */}
                  {bank?.map((item) => {
                    const isSelected = item.bank_id === selectedBankId;
                    const icon = banksIcons[getBankKey(item.bank_name)] || Card;

                    return (
                      <MenuItem
                        key={item.bank_id}
                        value={item.bank_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          fontWeight: isSelected ? 700 : 500,
                          '&.Mui-selected': {
                            background: theme.palette.gradients.buttonPrimary,
                            color: '#000',
                            '&:hover': {
                              background: theme.palette.gradients.buttonPrimary2,
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <img src={icon} alt="bank icons" width={40} height={40} />
                          {item.bank_name}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>

              {/* Account Holder */}
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('account_holder')}
                </Typography>

                <TextField
                  placeholder={translate('enter_here')}
                  fullWidth
                  size="small"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  sx={inputStyles}
                />
              </Box>

              {/* Account Number */}
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('account_number')}
                </Typography>

                <TextField
                  placeholder={translate('enter_here')}
                  fullWidth
                  size="small"
                  value={accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setAccountNumber(value);
                  }}
                  sx={inputStyles}
                />
              </Box>
            </Box>
          </Box>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          PaperProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
              <Iconify icon="material-symbols:arrow-back-ios-new-rounded" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('add_bank_account')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              height: '100%',
              background: isSafariOrIOS ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3,
              gap: 2,
            }}
          >
            <FormSection>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('preferred_bank')}
                </Typography>

                <Button
                  fullWidth
                  onClick={() => setPrefferedOpen(true)}
                  sx={{
                    justifyContent: 'space-between',
                    px: 2,
                    borderRadius: '12px',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    textTransform: 'none',
                    color: '#111827',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {/* ✅ ICON */}
                    <Box component="img" src={selectedIcon} alt="bank" sx={{ width: 32, height: 32 }} />

                    {/* ✅ NAME */}
                    <Typography fontWeight={500} fontSize="14px">
                      {selectedBank?.bank_name || translate('select_bank')}
                    </Typography>
                  </Stack>

                  <Iconify icon="mdi:chevron-right" width={20} />
                </Button>
              </Box>
            </FormSection>

            <FormSection>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('account_holder')}
                </Typography>

                <TextField
                  placeholder={translate('enter_here')}
                  fullWidth
                  size="small"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  sx={inputStyles}
                />
              </Box>
            </FormSection>

            <FormSection>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {translate('account_number')}
                </Typography>

                <TextField
                  placeholder={translate('enter_here')}
                  fullWidth
                  size="small"
                  value={accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setAccountNumber(value);
                  }}
                  sx={inputStyles}
                />
              </Box>
            </FormSection>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isValid || loading}
              fullWidth
              sx={{
                background: (theme) => theme.palette.gradients.buttonPrimary,
                textTransform: 'none',
                borderRadius: '30px',
                fontWeight: 600,
                fontSize: '16px',
                color: '#1a1a1a',
                boxShadow: 'none',

                '&:hover': {
                  background: theme.palette.gradients.buttonPrimary2,
                  boxShadow: 'none',
                },

                '&.Mui-disabled': {
                  background: '#F3F4F6 !important',
                  color: '#9CA3AF !important',
                },
              }}
            >
              {translate('submit')}
            </Button>
          </Box>
        </Drawer>
      )}

      <CustomDrawer open={prefferedOpen} onClose={() => setPrefferedOpen(false)} title={translate('preferred_bank')}>
        <Box
          sx={{
            p: 2,
            pb: 3,
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {bank?.map((banks) => {
            const icon = banksIcons[getBankKey(banks.bank_name)] || Card;

            const isSelected = selectedBankId === banks.bank_id;

            return (
              <Box
                key={banks.bank_id}
                onClick={() => {
                  setSelectedBankId(banks.bank_id);
                  setPrefferedOpen(false);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  mb: 1.5,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  background: isSelected ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  transition: 'all 0.2s',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  {/* ✅ ICON */}
                  <Box component="img" src={icon} alt={banks.bank_name} sx={{ width: 32, height: 32 }} />

                  {/* ✅ NAME */}
                  <Box
                    sx={{
                      fontWeight: isSelected ? 700 : 500,
                      color: '#111',
                    }}
                  >
                    {banks.bank_name}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CustomDrawer>
    </>
  );
};

export default AddBankAccountDialog;
